import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  isAfter,
  sub,
} from 'date-fns';

const differenceInFn = {
  second: differenceInSeconds,
  minute: differenceInMinutes,
  hour: differenceInHours,
  day: differenceInDays,
  month: differenceInMonths,
};
type possibleParts = keyof typeof differenceInFn;

export interface PreciseDiff {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  months: number;
}

export function preciseDifference(
  leftDate: Date,
  rightDate: Date,
  parts: possibleParts[] = ['month', 'day', 'hour', 'minute'],
): PreciseDiff | null {
  if (isAfter(leftDate, rightDate)) {
    return preciseDifference(rightDate, leftDate, parts);
  }
  const response: PreciseDiff = {
    seconds: 0,
    minutes: 0,
    hours: 0,
    days: 0,
    months: 0,
  };

  let rightCopy = add(rightDate, { seconds: 0 });

  parts.forEach((part, i) => {
    const fn = differenceInFn[part];
    if (fn) {
      const differenceInPart = fn(rightCopy, leftDate);
      if (differenceInPart) {
        response[`${part}s`] = differenceInPart;
        const subtractionDuration = { [`${part}s`]: differenceInPart };
        // We are just going to subtract from "now" until we get down to the date
        rightCopy = sub(rightCopy, subtractionDuration);
      }
    }
  });
  return response;
}

const dateOrder: (keyof PreciseDiff)[] = ['months', 'days', 'hours', 'minutes'];

const substitutes: Partial<Record<keyof PreciseDiff, string>> = {
  months: 'mo',
  minutes: 'm',
  hours: 'h',
  days: 'd',
  seconds: 's',
};

export function prettyPrintPreciseDiff(preciseDiff: PreciseDiff, maxParts = 2, suffix = '', useShort = true): string {
  let response = '0 minutes';
  if (useShort) {
    response = '0m';
  }
  const result = [];
  let dateOrderIndex = 0;
  while (dateOrderIndex < dateOrder.length && result.length < maxParts) {
    const key = dateOrder[dateOrderIndex];
    if (preciseDiff[key] > 0) {
      const keyName = substitutes[key] && useShort ? substitutes[key] : key;
      if (useShort) {
        result.push(`${preciseDiff[key]}${removePluralIfNeeded(preciseDiff[key], keyName)}`);
      } else {
        result.push(`${preciseDiff[key]} ${removePluralIfNeeded(preciseDiff[key], keyName)}`);
      }
    }
    dateOrderIndex++;
  }
  if (result.length) {
    response = `${result.join(' ')} ${suffix}`;
  }

  return response;
}

export function prettyPrintPreciseDiffToSeconds(
  preciseDiff: PreciseDiff,
  maxParts = 2,
  suffix = '',
  useShort = true,
): string {
  let response = '0 seconds';
  if (useShort) {
    response = '0s';
  }
  const result = [];
  let dateOrderIndex = 0;
  const dateOrderInternal = [...dateOrder, 'seconds'];
  const internalSubstitutes = {
    ...substitutes,
    seconds: 's',
  };
  while (dateOrderIndex < dateOrderInternal.length && result.length < maxParts) {
    const key = dateOrderInternal[dateOrderIndex];
    if (preciseDiff[key] > 0) {
      const keyName = internalSubstitutes[key] && useShort ? internalSubstitutes[key] : key;
      if (useShort) {
        result.push(`${preciseDiff[key]}${removePluralIfNeeded(preciseDiff[key], keyName)}`);
      } else {
        result.push(`${preciseDiff[key]} ${removePluralIfNeeded(preciseDiff[key], keyName)}`);
      }
    }
    dateOrderIndex++;
  }
  if (result.length) {
    response = `${result.join(' ')} ${suffix}`;
  }

  return response;
}

function removePluralIfNeeded(count: number, pluralString: string) {
  if (count !== 1 || !pluralString.endsWith('s') || pluralString.length === 1) {
    return pluralString;
  }
  return pluralString.substring(0, pluralString.length - 1);
}
